/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Fab,
  Stack,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useRef } from "react";
import { EnrAutoComplete } from "../basic/EnrAutoComplete";
import { debounce } from "lodash";
import { api } from "../../api/api";
import { IEventTime } from "../../redux/common/common.types";
import { defaultEventTime } from "../../redux/common/common.default";
import AddIcon from "@mui/icons-material/Add";
import { IEventTimeAutoSearchProps } from "./AutoCompleteSearches.types";

export const EventTimeAutoSearch: React.FC<IEventTimeAutoSearchProps> = (
  props
) => {
  const {
    label,
    value,
    onSelect,
    event_name,
    disabled,
    hideAddButton,
    error,
    helperText,
    fullWidth,
  } = props;
  const [options, setOptions] = React.useState<readonly IEventTime[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearchText] = React.useState<any>("");
  const [openRightPanel, setOpenRightPanel] = React.useState<boolean>(false);
  const initialLoad = useRef(false);

  const fetchSuggestion = async (enteredValue: string) => {
    setLoading(true);
    try {
      let searchQuery = "";
      if (enteredValue.length > 0) {
        searchQuery = `columns=event_name&value=${enteredValue}&`;
      }
      const res = await api.get(`/occupancy/get-event-timing?${searchQuery}`);
      const data: IEventTime[] = res.data.data;
      setOptions(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  const debounceFn = React.useMemo(() => debounce(fetchSuggestion, 800), []);
  React.useEffect(() => () => debounceFn.cancel(), [debounceFn]);

  const getOptionLabel = (option: string | IEventTime) => {
    if (typeof option === "string") {
      return option;
    }
    if (option.event_name) {
      return `${option.event_name}`;
    }
    return `${option.event_timing_uuid || ""}`;
  };

  const handleToggle = () => {
    setOpenRightPanel(!setOpenRightPanel);
  };

  const handleFocus = () => {
    if (!initialLoad.current) {
      fetchSuggestion("");
      initialLoad.current = true;
    }
  };

  useEffect(() => {
    if (search && search !== value) {
      debounceFn(search);
    }
  }, [search, value, debounceFn]);

  return (
    <Stack direction={"row"} justifyContent={"space-between"} spacing={1}>
      <EnrAutoComplete<IEventTime, false, false, true>
        id="event-time-auto-search"
        fullWidth
        freeSolo
        label={label}
        error={typeof error === "string" ? error : undefined}
        helperText={helperText}
        loading={loading}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, value) =>
          typeof option !== "string" && typeof value !== "string"
            ? option.event_name === value.event_name
            : option === value
        }
        filterOptions={(x) => x}
        disabled={disabled}
        options={options as IEventTime[]}
        onFocus={handleFocus} // Only fetch data on first focus
        includeInputInList
        value={
          (options.find((option) => option.event_timing_uuid === value) ||
            event_name ||
            null) as any
        }
        noOptionsText="No Items"
        onChange={(event, value: string | IEventTime | null) => {
          if (typeof value === "object" && value !== null) {
            onSelect?.(value);
          } else {
            onSelect?.(defaultEventTime);
          }
        }}
        onInputChange={(event, newInputValue) => {
          if ((event && event.type === "change") || !newInputValue) {
            setSearchText(newInputValue);
          }
        }}
      />
      {!label &&
        !hideAddButton &&
        search.length > 0 &&
        options.length === 0 &&
        !loading && (
          <Box>
            <Tooltip title="Create Product">
              <Fab
                size="small"
                color="primary"
                aria-label="add"
                onClick={handleToggle}
              >
                <AddIcon />
              </Fab>
            </Tooltip>
          </Box>
        )}
    </Stack>
  );
};
